import { FC, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { NumberField } from "components/inputs/NumberField"
import { ToggleCard } from "components/inputs/ToggleCard"

import { settingsService } from "services/settings/settings.service"
import { GiftSettings } from "services/settings/type"

import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

interface GiftsTabProps {
  gifts: GiftSettings
  onChange: (gifts: GiftSettings) => void
  refreshSettings: () => Promise<void>
  onSaved?: () => void
}

export const GiftsTab: FC<GiftsTabProps> = ({ gifts, onChange, refreshSettings, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  const handleSave = async () => {
    setSaving(true)
    try {
      const success = await settingsService.updateGifts(gifts)
      if (success) {
        toast.success(t("settings.gifts.updated") as string)
        onSaved?.()
        setHistoryKey((key) => key + 1)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleResetDone = async () => {
    await refreshSettings()
    setHistoryKey((key) => key + 1)
  }

  return (
    <section className="flex flex-col gap-6 mt-6">
      <h3 className="text-xl font-semibold mb-2 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.gifts.title")}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 -mt-2">{t("settings.gifts.description")}</p>
      <SettingsHistoryNote target="gifts" refreshKey={historyKey} />

      <ToggleCard
        label={t("settings.gifts.enableFeature")}
        checked={gifts.enabled}
        onChange={() => onChange({ ...gifts, enabled: !gifts.enabled })}
        disabled={saving}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
        <NumberField
          id="gifts-min-send"
          label={t("settings.gifts.minTipAmount")}
          value={gifts.minSendCents}
          onChange={(v) => onChange({ ...gifts, minSendCents: parseFloat(v) || 0 })}
          disabled={saving}
          centsDisplay
          hint={t("settings.gifts.minTipAmountHint")}
        />
        <NumberField
          id="gifts-max-send"
          label={t("settings.gifts.maxTipAmount")}
          value={gifts.maxSendCents}
          onChange={(v) => onChange({ ...gifts, maxSendCents: parseFloat(v) || 0 })}
          disabled={saving}
          centsDisplay
          hint={t("settings.gifts.maxTipAmountHint")}
        />
        <NumberField
          id="gifts-min-deposit"
          label={t("settings.gifts.minDepositThreshold")}
          value={gifts.minDepositCents}
          onChange={(v) => onChange({ ...gifts, minDepositCents: parseFloat(v) || 0 })}
          disabled={saving}
          centsDisplay
          hint={t("settings.gifts.minDepositThresholdHint")}
        />
      </div>

      <div className="bg-warning/10 border-warning px-6 py-3 w-fit">
        <p className="text-sm text-warning">{t("settings.gifts.warning")}</p>
      </div>

      <SettingsSectionActions
        onSave={handleSave}
        saving={saving}
        resetTarget="gifts"
        resetLabel={t("settings.gifts.title")}
        onResetDone={handleResetDone}
      />
    </section>
  )
}
